import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { checkinAPI } from '../services/api';

const DailyCheckin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    mood_score: 5,
    sleep_hours: 7,
    stress_level: 5,
    anxiety_level: 5,
    energy_level: 5,
    notes: '',
    exercise_minutes: 0,
    water_intake_liters: 2,
    meditation_minutes: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkTodayCheckin();
  }, []);

  const checkTodayCheckin = async () => {
    try {
      const response = await checkinAPI.getToday();
      setTodayCheckin(response.data.checkin);
    } catch (error) {
      console.error('Error checking today\'s check-in:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Call the actual API
      const response = await checkinAPI.create(formData);
      
      if (response.data) {
        toast.success('Daily check-in completed successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Check-in error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit check-in. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const moodEmojis = ['😢', '😔', '😐', '🙂', '😊'];

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="bg-white rounded-xl shadow-md p-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (todayCheckin) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="bg-white rounded-xl shadow-md p-8">
          <div className="text-center">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Check-in Completed!</h1>
            <p className="text-gray-600 mb-8">You've already completed your daily check-in for today.</p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-green-800 mb-4">Today's Check-in Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-left">
                <div>
                  <span className="text-gray-600 text-sm">Mood</span>
                  <div className="text-2xl font-bold text-gray-900">
                    {moodEmojis[todayCheckin.mood_score - 1]} ({todayCheckin.mood_score}/10)
                  </div>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Sleep</span>
                  <div className="text-2xl font-bold text-gray-900">{todayCheckin.sleep_hours} hrs</div>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Stress</span>
                  <div className="text-2xl font-bold text-gray-900">{todayCheckin.stress_level}/10</div>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Streak</span>
                  <div className="text-2xl font-bold text-green-600">{todayCheckin.streak || 1} days</div>
                </div>
              </div>
              {todayCheckin.notes && (
                <div className="mt-4 text-left">
                  <span className="text-gray-600 text-sm">Notes</span>
                  <p className="text-gray-900 mt-1">{todayCheckin.notes}</p>
                </div>
              )}
            </div>
            
            <div className="flex space-x-4 justify-center">
              <button
                onClick={() => navigate('/analytics')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Analytics
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="bg-white rounded-xl shadow-md p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Daily Check-in</h1>
        <p className="text-gray-600 mb-8">How are you feeling today? Track your wellness metrics.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Mood Selection */}
          <div>
            <label className="block text-lg font-medium text-gray-900 mb-4">How's your mood today?</label>
            <div className="flex justify-center space-x-4 mb-2">
              {moodEmojis.map((emoji, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleInputChange('mood_score', index + 1)}
                  className={`text-4xl p-3 rounded-lg transition-all ${
                    formData.mood_score === index + 1 
                      ? 'bg-blue-100 ring-2 ring-blue-500 scale-110' 
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <div className="text-center text-sm text-gray-600">
              {formData.mood_score <= 2 && 'Having a tough day?'}
              {formData.mood_score === 3 && 'Okay day'}
              {formData.mood_score >= 4 && 'Great to hear you\'re doing well!'}
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sleep Hours
              </label>
              <input
                type="number"
                min="0"
                max="24"
                step="0.5"
                value={formData.sleep_hours}
                onChange={(e) => handleInputChange('sleep_hours', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stress Level (0-10)
              </label>
              <input
                type="range"
                min="0"
                max="10"
                value={formData.stress_level}
                onChange={(e) => handleInputChange('stress_level', parseInt(e.target.value))}
                className="w-full"
              />
              <div className="text-center text-sm text-gray-600">{formData.stress_level}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anxiety Level (0-10)
              </label>
              <input
                type="range"
                min="0"
                max="10"
                value={formData.anxiety_level}
                onChange={(e) => handleInputChange('anxiety_level', parseInt(e.target.value))}
                className="w-full"
              />
              <div className="text-center text-sm text-gray-600">{formData.anxiety_level}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Energy Level (1-10)
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.energy_level}
                onChange={(e) => handleInputChange('energy_level', parseInt(e.target.value))}
                className="w-full"
              />
              <div className="text-center text-sm text-gray-600">{formData.energy_level}</div>
            </div>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Exercise Minutes
              </label>
              <input
                type="number"
                min="0"
                value={formData.exercise_minutes}
                onChange={(e) => handleInputChange('exercise_minutes', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Water Intake (Liters)
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={formData.water_intake_liters}
                onChange={(e) => handleInputChange('water_intake_liters', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meditation Minutes
              </label>
              <input
                type="number"
                min="0"
                value={formData.meditation_minutes}
                onChange={(e) => handleInputChange('meditation_minutes', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Daily Notes (Optional)
            </label>
            <textarea
              rows="4"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="How was your day? Any achievements or challenges?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Complete Check-in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DailyCheckin;
